import React, { useState, useEffect, useRef, useCallback } from 'react';
import { promptService } from '../services/promptService';
import bedrockService from '../services/bedrockService';
import fileService from '../services/fileService';
import reviewsService from '../services/reviewsService';

// ---- Constants ---------------------------------------------------------
const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/plain',
  'text/csv',
];

const MAX_FILES = 5;

const STEPS = ['upload', 'analyzing', 'review', 'compare'];

// ---- Helpers -----------------------------------------------------------

/** Convert a raw `fileSize` bytes number to a human-readable string. */
const fmtBytes = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Parse a bedrock text response into sections: [{id, collapsed, items: [{key,value}]}].
 * When the JSON has nested objects each top-level key becomes a collapsible section.
 * Falls back to a single '_root' section for flat JSON or colon-delimited text.
 */
const parseResponse = (text) => {
  const cleaned = text.replace(/^```[\w]*\n?/m, '').replace(/```\s*$/m, '').trim();

  const makeSection = (id, items, collapsed = false) => ({ id, collapsed, items });
  const toVal = (v) => (v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v));

  try {
    const j = JSON.parse(cleaned);
    if (j && typeof j === 'object' && !Array.isArray(j)) {
      const hasNested = Object.values(j).some(
        (v) => v && typeof v === 'object' && !Array.isArray(v)
      );
      if (hasNested) {
        return Object.entries(j).map(([sectionKey, sectionVal]) =>
          makeSection(
            sectionKey,
            sectionVal && typeof sectionVal === 'object' && !Array.isArray(sectionVal)
              ? Object.entries(sectionVal).map(([k, v]) => ({ key: k, value: toVal(v) }))
              : [{ key: sectionKey, value: toVal(sectionVal) }]
          )
        );
      }
      // Flat JSON
      return [makeSection('_root', Object.entries(j).map(([k, v]) => ({ key: k, value: toVal(v) })))];
    }
    if (Array.isArray(j)) {
      const items = j
        .filter((item) => item && typeof item === 'object' && 'key' in item)
        .map((item) => ({ key: String(item.key), value: String(item.value ?? '') }));
      return [makeSection('_root', items)];
    }
  } catch (_) {}

  // Colon-delimited lines
  const lines = cleaned.split('\n').filter(Boolean);
  const pairs = [];
  for (const line of lines) {
    const colonIdx = line.indexOf(':');
    if (colonIdx > 0) {
      const rawKey = line.slice(0, colonIdx).trim().replace(/^[*#\s\-]+/, '').replace(/[*]+/g, '');
      const value = line.slice(colonIdx + 1).trim();
      if (rawKey && rawKey.length < 80) pairs.push({ key: rawKey, value });
    }
  }
  if (pairs.length > 0) return [makeSection('_root', pairs)];

  return [makeSection('_root', [{ key: 'Analysis Result', value: text }])];
};

/** Format a section id (snake_case) into a readable label. */
const formatSectionLabel = (id) => {
  if (id === '_root') return 'Fields';
  if (id === '_meta') return 'Meta';
  return id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// ---- Step indicator ----------------------------------------------------
const StepIndicator = ({ step }) => {
  const steps = [
    { id: 'upload', label: 'Upload & Configure' },
    { id: 'analyzing', label: 'Analyzing' },
    { id: 'review', label: 'Review & Edit' },
    { id: 'compare', label: 'Verify & Save' },
  ];
  const currentIdx = STEPS.indexOf(step);

  return (
    <div className="d-flex align-items-center justify-content-center mb-3" style={{ gap: 0 }}>
      {steps.map((s, idx) => {
        const done = idx < currentIdx;
        const active = idx === currentIdx;
        return (
          <React.Fragment key={s.id}>
            <div className="d-flex flex-column align-items-center" style={{ minWidth: 90 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  backgroundColor: done ? '#c9a84c' : active ? '#f0c040' : '#2a2200',
                  border: `2px solid ${active || done ? '#c9a84c' : '#3a2e00'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  color: done || active ? '#0a0900' : '#7a6a40',
                }}
              >
                {done ? <i className="bi bi-check" /> : idx + 1}
              </div>
              <span
                style={{
                  fontSize: '0.6rem',
                  marginTop: 4,
                  color: active ? '#f0c040' : done ? '#c9a84c' : '#7a6a40',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                }}
              >
                {s.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div
                style={{
                  height: 2,
                  width: 48,
                  backgroundColor: idx < currentIdx ? '#c9a84c' : '#2a2200',
                  marginBottom: 20,
                  flexShrink: 0,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ---- Main modal --------------------------------------------------------
export default function EarningsIngestModal({ isOpen, onClose, companyName, companyId, onIngestComplete }) {
  // Step
  const [step, setStep] = useState('upload');

  // File upload
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState('');
  const inputRef = useRef(null);

  // Prompts
  const [earningsPrompts, setEarningsPrompts] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState('');
  const [promptText, setPromptText] = useState('');
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [promptError, setPromptError] = useState('');

  // Analysis
  const [analysisStatus, setAnalysisStatus] = useState('');
  const [analysisError, setAnalysisError] = useState('');

  // Review sections
  const [sections, setSections] = useState([]); // [{id, collapsed, items: [{key, value}]}]
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newSectionTarget, setNewSectionTarget] = useState('');
  const [activeDocTab, setActiveDocTab] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Compare
  const [compareResults, setCompareResults] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState('');

  // ---- Lifecycle ---------------------------------------------------------
  useEffect(() => {
    if (isOpen) {
      setStep('upload');
      setFiles([]);
      setFileError('');
      setAnalysisError('');
      setSections([]);
      setNewSectionTarget('');
      setNewKey('');
      setNewValue('');
      setActiveDocTab(0);
      setCompareResults(null);
      setCompareLoading(false);
      setCompareError('');
      loadPrompts();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPrompts = useCallback(async () => {
    setLoadingPrompts(true);
    setPromptError('');
    try {
      const data = await promptService.getPrompts(null, true);
      const list = (data.prompts || []).filter((p) => p.name?.endsWith('_Earnings'));
      setEarningsPrompts(list);

      // Determine default
      // 'Test_Earnings' is a QA testing fallback: its name is unclaimed in the
      // shared Bedrock account, so it avoids the timestamp-suffix mangling that
      // breaks the `_Earnings` filter for names already taken by prod.
      const preferred = [`${companyName}_Earnings`, 'Default_Earnings', 'Test_Earnings'];
      let defaultPrompt = null;
      for (const name of preferred) {
        defaultPrompt = list.find((p) => p.name === name);
        if (defaultPrompt) break;
      }
      if (!defaultPrompt && list.length > 0) defaultPrompt = list[0];

      if (defaultPrompt) {
        const promptId = defaultPrompt.id || defaultPrompt.prompt_id;
        setSelectedPromptId(promptId);
        await fetchPromptText(promptId, defaultPrompt);
      }
    } catch (e) {
      setPromptError('Failed to load prompts. You can type your prompt manually below.');
    } finally {
      setLoadingPrompts(false);
    }
  }, [companyName]);

  const fetchPromptText = async (promptId, promptObj) => {
    // Use lightweight summary content if available
    if (promptObj?.user_message) {
      setPromptText(promptObj.user_message);
      return;
    }
    if (promptObj?.content) {
      setPromptText(promptObj.content);
      return;
    }
    // Fetch full prompt
    try {
      const full = await promptService.getPrompt(promptId);
      // The API returns the prompt object under full.prompt (Prompt model),
      // or the full response itself may be the prompt. The text field is "prompt".
      const p = full.prompt || full;
      setPromptText(p.prompt || p.user_message || p.content || '');
    } catch (_) {}
  };

  const handlePromptSelect = async (e) => {
    const promptId = e.target.value;
    setSelectedPromptId(promptId);
    const found = earningsPrompts.find((p) => (p.id || p.prompt_id) === promptId);
    if (found) await fetchPromptText(promptId, found);
  };

  // ---- File handling -----------------------------------------------------
  const validateAndAdd = (incoming) => {
    setFileError('');
    const combined = [...files];
    const rejected = [];

    for (const f of incoming) {
      if (combined.length >= MAX_FILES) {
        rejected.push(f.name);
        continue;
      }
      if (!ACCEPTED_TYPES.includes(f.type)) {
        rejected.push(`${f.name} (unsupported type)`);
        continue;
      }
      if (combined.some((ex) => ex.name === f.name)) continue;
      combined.push(f);
    }

    setFiles(combined);
    if (rejected.length > 0) {
      setFileError(
        `Skipped: ${rejected.join(', ')}. Accepted: PDF, DOCX, XLSX, TXT, CSV.`
      );
    }
  };

  const removeFile = (name) => setFiles((prev) => prev.filter((f) => f.name !== name));

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  const handleDragLeave = () => setDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    validateAndAdd(Array.from(e.dataTransfer.files));
  };
  const handleInputChange = (e) => {
    validateAndAdd(Array.from(e.target.files));
    e.target.value = '';
  };

  // ---- Analyze -----------------------------------------------------------
  const handleAnalyze = async () => {
    if (files.length === 0) {
      setFileError('Please upload at least one document.');
      return;
    }
    if (!selectedPromptId || !promptText.trim()) {
      setPromptError('Please select a prompt.');
      return;
    }

    setStep('analyzing');
    setAnalysisError('');

    try {
      const sessionId = crypto.randomUUID();

      setAnalysisStatus(`Uploading ${files.length} document${files.length > 1 ? 's' : ''}…`);
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const customPath = `temp-doc-chat/${sessionId}/${safeName}`;
        setAnalysisStatus(`Uploading ${i + 1}/${files.length}: ${file.name}…`);
        await fileService.uploadFileToPath(file, customPath);
      }

      setAnalysisStatus('Running AI analysis — this may take a moment…');
      const { job_id } = await bedrockService.submitDocChatQuery(
        sessionId,
        promptText.trim(),
        [],
        selectedPromptId ? { prompt_id: selectedPromptId } : {},
      );

      // Poll for completion
      const POLL_MS = 2500;
      const DEADLINE = Date.now() + 600_000; // 10 min
      let result = null;
      while (Date.now() < DEADLINE) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        const status = await bedrockService.getDocChatJobStatus(job_id);
        if (status.status === 'completed') {
          result = status;
          break;
        }
        if (status.status === 'failed') {
          throw new Error(status.error || 'Analysis failed. Please try again.');
        }
      }

      if (!result) throw new Error('Analysis timed out. Please try again.');

      const parsed = parseResponse(result.answer);
      setSections(parsed);
      setNewSectionTarget(parsed[0]?.id ?? '_root');
      setActiveDocTab(0);
      setStep('review');
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || 'Analysis failed. Please try again.';
      setAnalysisError(msg);
      setStep('upload');
    }
  };

  // ---- Review helpers ----------------------------------------------------
  const updateItem = (sectionIdx, itemIdx, field, val) => {
    setSections((prev) =>
      prev.map((s, si) =>
        si !== sectionIdx
          ? s
          : { ...s, items: s.items.map((item, ii) => (ii !== itemIdx ? item : { ...item, [field]: val })) }
      )
    );
  };

  const removeItem = (sectionIdx, itemIdx) => {
    setSections((prev) =>
      prev.map((s, si) =>
        si !== sectionIdx ? s : { ...s, items: s.items.filter((_, ii) => ii !== itemIdx) }
      )
    );
  };

  const toggleSection = (sectionIdx) => {
    setSections((prev) =>
      prev.map((s, si) => (si !== sectionIdx ? s : { ...s, collapsed: !s.collapsed }))
    );
  };

  const addItem = () => {
    const k = newKey.trim();
    const v = newValue.trim();
    if (!k) return;
    const targetId = newSectionTarget || sections[0]?.id || '_root';
    setSections((prev) =>
      prev.map((s) => (s.id !== targetId ? s : { ...s, items: [...s.items, { key: k, value: v }] }))
    );
    setNewKey('');
    setNewValue('');
  };

  const handleSubmit = () => {
    setSubmitting(true);
    const result = {};
    for (const section of sections) {
      if (section.id === '_root') {
        for (const { key, value } of section.items) result[key] = value;
      } else {
        result[section.id] = section.items.reduce((acc, { key, value }) => ({ ...acc, [key]: value }), {});
      }
    }
    onIngestComplete?.(result, files.map((f) => f.name));
    onClose();
  };

  // ---- Build sections dict from current React state ----------------------
  const buildSectionsDict = () => {
    const dict = {};
    for (const section of sections) {
      if (section.id === '_root') {
        for (const { key, value } of section.items) dict[key] = value;
      } else {
        dict[section.id] = section.items.reduce(
          (acc, { key, value }) => ({ ...acc, [key]: value }),
          {}
        );
      }
    }
    return dict;
  };

  // ---- Verify: call compare endpoint then show compare step -------------
  const handleVerify = async () => {
    const sectionsDict = buildSectionsDict();
    const meta = sectionsDict._meta || sectionsDict._root || {};
    const periodEndDate =
      (typeof meta === 'object' ? meta.period_end_date : null) ||
      new Date().toISOString().slice(0, 10);

    setCompareLoading(true);
    setCompareError('');
    setCompareResults(null);
    setStep('compare');

    try {
      const results = await reviewsService.compareEarnings(
        companyId,
        periodEndDate,
        sectionsDict
      );
      setCompareResults(results);
    } catch (err) {
      setCompareError(
        err?.response?.data?.detail || err?.message || 'Comparison failed.'
      );
    } finally {
      setCompareLoading(false);
    }
  };

  // ---- Confirm & Save: save only new quarter(s) -------------------------
  const handleConfirmSave = () => {
    const fullSections = buildSectionsDict();

    let filteredSections = {};
    const newQuarters = compareResults?.quarters?.filter((q) => q.is_new) ?? [];

    if (newQuarters.length === 0 || newQuarters.some((q) => q.section_key === null)) {
      // No named quarter sections — save the whole thing
      filteredSections = fullSections;
    } else {
      // Named section mode: only include new quarter sections
      for (const q of newQuarters) {
        if (q.section_key && fullSections[q.section_key] !== undefined) {
          filteredSections[q.section_key] = fullSections[q.section_key];
        }
      }
      if (fullSections._meta) filteredSections._meta = fullSections._meta;
      if (fullSections._root) filteredSections._root = fullSections._root;
    }

    // Ensure _meta carries period_end_date so the parent component can save correctly
    const primaryPeriod = compareResults?.current_period_end_date;
    if (primaryPeriod) {
      if (!filteredSections._meta) filteredSections._meta = {};
      if (!filteredSections._meta.period_end_date) {
        filteredSections._meta.period_end_date = primaryPeriod;
      }
    }

    onIngestComplete?.(filteredSections, files.map((f) => f.name));
    onClose();
  };

  // Derived counts
  const totalItems = sections.reduce((acc, s) => acc + s.items.length, 0);

  // ---- Guard -------------------------------------------------------------
  if (!isOpen) return null;

  // ---- Styles ------------------------------------------------------------
  const dark = {
    bg: '#0a0900',
    card: '#111008',
    border: '#3a2e00',
    gold: '#c9a84c',
    goldBright: '#f0c040',
    muted: '#7a6a40',
    mutedLight: '#9a8a60',
    text: '#fff',
  };

  const inputStyle = {
    backgroundColor: '#1a1500',
    color: dark.text,
    border: `1px solid ${dark.border}`,
    fontSize: '0.85rem',
  };

  const labelStyle = {
    fontSize: '0.7rem',
    color: dark.mutedLight,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 4,
  };

  // ---- Render ------------------------------------------------------------
  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}
        onClick={step !== 'analyzing' ? onClose : undefined}
      />

      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        style={{ zIndex: 1055 }}
        onClick={(e) => {
          if (e.target === e.currentTarget && step !== 'analyzing') onClose();
        }}
      >
        <div
          className="modal-dialog modal-xl modal-dialog-scrollable"
          style={{ maxWidth: 880 }}
          role="document"
        >
          <div
            className="modal-content"
            style={{
              backgroundColor: dark.bg,
              border: `1px solid ${dark.border}`,
              color: dark.text,
            }}
          >
            {/* Header */}
            <div
              className="modal-header"
              style={{ borderBottom: `1px solid ${dark.border}` }}
            >
              <h5 className="modal-title" style={{ fontSize: '1rem', fontWeight: 700 }}>
                <span style={{ color: dark.gold, marginRight: 8 }}>$</span>
                Ingest Earnings Data
                {companyName && (
                  <span style={{ color: dark.muted, fontWeight: 400, marginLeft: 8, fontSize: '0.85rem' }}>
                    — {companyName}
                  </span>
                )}
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
                disabled={step === 'analyzing'}
                aria-label="Close"
              />
            </div>

            {/* Step indicator */}
            <div
              className="px-4 pt-3"
              style={{ borderBottom: `1px solid ${dark.border}`, paddingBottom: 4 }}
            >
              <StepIndicator step={step} />
            </div>

            {/* ================================================================
                STEP 1 — Upload & Configure
            ================================================================ */}
            {step === 'upload' && (
              <div className="modal-body" style={{ padding: '20px 24px' }}>
                {analysisError && (
                  <div
                    className="alert mb-3 py-2"
                    style={{
                      backgroundColor: '#2a0800',
                      border: '1px solid #8b3300',
                      color: '#ff9966',
                      fontSize: '0.83rem',
                    }}
                  >
                    <i className="bi bi-exclamation-triangle me-2" />
                    {analysisError}
                  </div>
                )}

                {/* Drop zone */}
                <div style={labelStyle}>Documents (1–{MAX_FILES})</div>
                <div
                  style={{
                    border: `2px dashed ${dragOver ? dark.goldBright : dark.border}`,
                    borderRadius: 8,
                    padding: '20px 16px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: dragOver ? 'rgba(240,192,64,0.05)' : dark.card,
                    transition: 'border-color 0.15s, background 0.15s',
                    marginBottom: 8,
                  }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => inputRef.current?.click()}
                >
                  <i
                    className="bi bi-cloud-arrow-up"
                    style={{ fontSize: '1.8rem', color: dark.muted, display: 'block', marginBottom: 6 }}
                  />
                  <div style={{ fontSize: '0.85rem', color: dark.mutedLight }}>
                    Drag &amp; drop earnings documents here, or{' '}
                    <span style={{ color: dark.gold }}>click to browse</span>
                  </div>
                  <div style={{ fontSize: '0.72rem', color: dark.muted, marginTop: 4 }}>
                    PDF · DOCX · XLSX · TXT · CSV &nbsp;·&nbsp; Up to {MAX_FILES} files
                  </div>
                  <input
                    ref={inputRef}
                    type="file"
                    multiple
                    accept={ACCEPTED_TYPES.join(',')}
                    className="d-none"
                    onChange={handleInputChange}
                  />
                </div>

                {/* File list */}
                {files.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    {files.map((f) => (
                      <div
                        key={f.name}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          backgroundColor: '#1a1500',
                          border: `1px solid ${dark.border}`,
                          borderRadius: 6,
                          padding: '3px 10px',
                          fontSize: '0.75rem',
                          color: dark.gold,
                        }}
                      >
                        <i className="bi bi-file-earmark-text" style={{ fontSize: '0.8rem' }} />
                        {f.name}
                        <span style={{ color: dark.muted }}>({fmtBytes(f.size)})</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile(f.name);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: dark.muted,
                            cursor: 'pointer',
                            padding: 0,
                            fontSize: '0.8rem',
                            lineHeight: 1,
                          }}
                          aria-label={`Remove ${f.name}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {fileError && (
                  <div style={{ fontSize: '0.75rem', color: '#ff9966', marginBottom: 8 }}>
                    <i className="bi bi-exclamation-triangle me-1" />
                    {fileError}
                  </div>
                )}

                {/* Divider */}
                <hr style={{ borderColor: dark.border, margin: '16px 0' }} />

                {/* Prompt selector */}
                <div style={labelStyle}>Earnings Prompt</div>
                {loadingPrompts ? (
                  <div style={{ fontSize: '0.8rem', color: dark.muted, marginBottom: 8 }}>
                    <span className="spinner-border spinner-border-sm me-2" style={{ color: dark.gold }} />
                    Loading prompts…
                  </div>
                ) : (
                  <select
                    className="form-select form-select-sm mb-2"
                    style={inputStyle}
                    value={selectedPromptId}
                    onChange={handlePromptSelect}
                  >
                    {earningsPrompts.length === 0 && (
                      <option value="">No _Earnings prompts found</option>
                    )}
                    {earningsPrompts.map((p) => {
                      const id = p.id || p.prompt_id;
                      return (
                        <option key={id} value={id}>
                          {p.name}
                        </option>
                      );
                    })}
                  </select>
                )}

                {promptError && (
                  <div style={{ fontSize: '0.75rem', color: '#ff9966', marginBottom: 6 }}>
                    <i className="bi bi-exclamation-triangle me-1" />
                    {promptError}
                  </div>
                )}


              </div>
            )}

            {/* ================================================================
                STEP 2 — Analyzing
            ================================================================ */}
            {step === 'analyzing' && (
              <div
                className="modal-body d-flex flex-column align-items-center justify-content-center"
                style={{ minHeight: 280, padding: '40px 24px' }}
              >
                <div
                  className="spinner-border mb-4"
                  style={{ color: dark.gold, width: '3rem', height: '3rem' }}
                  role="status"
                />
                <div style={{ fontSize: '1rem', fontWeight: 600, color: dark.goldBright, marginBottom: 8 }}>
                  Analyzing Documents
                </div>
                <div style={{ fontSize: '0.85rem', color: dark.muted, textAlign: 'center', maxWidth: 400 }}>
                  {analysisStatus || 'Processing…'}
                </div>
              </div>
            )}

            {/* ================================================================
                STEP 3 — Review & Edit
            ================================================================ */}
            {step === 'review' && (
              <div className="modal-body" style={{ padding: '16px 24px' }}>
                <div className="row g-3">
                  {/* Left: key-value form */}
                  <div className="col-md-7">
                    <div style={labelStyle}>Extracted Data — Review &amp; Edit</div>
                    <div
                      style={{
                        border: `1px solid ${dark.border}`,
                        borderRadius: 8,
                        overflow: 'hidden',
                        maxHeight: 480,
                        overflowY: 'auto',
                      }}
                    >
                      {sections.length === 0 && (
                        <div style={{ padding: '20px', textAlign: 'center', color: dark.muted, fontSize: '0.8rem' }}>
                          No fields extracted. Add them manually below.
                        </div>
                      )}
                      {sections.map((section, sectionIdx) => (
                        <div key={section.id} style={{ borderBottom: `1px solid ${dark.border}` }}>
                          {/* Section header */}
                          <button
                            type="button"
                            onClick={() => toggleSection(sectionIdx)}
                            style={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '7px 10px',
                              background: '#0f0c02',
                              border: 'none',
                              borderBottom: section.collapsed ? 'none' : `1px solid ${dark.border}`,
                              color: dark.goldBright,
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              letterSpacing: '0.07em',
                              textTransform: 'uppercase',
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                          >
                            <span>
                              <i className={`bi bi-chevron-${section.collapsed ? 'right' : 'down'} me-2`} />
                              {formatSectionLabel(section.id)}
                            </span>
                            <span style={{ color: dark.muted, fontWeight: 400, fontSize: '0.69rem' }}>
                              {section.items.length} field{section.items.length !== 1 ? 's' : ''}
                            </span>
                          </button>

                          {/* Section items */}
                          {!section.collapsed && section.items.map((item, itemIdx) => (
                            <div
                              key={itemIdx}
                              className="d-flex align-items-start gap-2"
                              style={{
                                padding: '6px 10px',
                                borderBottom: itemIdx < section.items.length - 1 ? `1px solid ${dark.border}` : 'none',
                                backgroundColor: itemIdx % 2 === 0 ? dark.card : '#0c0b02',
                              }}
                            >
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                style={{ ...inputStyle, flex: '0 0 36%', fontWeight: 600, fontSize: '0.73rem' }}
                                value={item.key}
                                onChange={(e) => updateItem(sectionIdx, itemIdx, 'key', e.target.value)}
                              />
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                style={{ ...inputStyle, flex: 1, fontSize: '0.73rem' }}
                                value={item.value}
                                onChange={(e) => updateItem(sectionIdx, itemIdx, 'value', e.target.value)}
                              />
                              <button
                                type="button"
                                onClick={() => removeItem(sectionIdx, itemIdx)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: dark.muted,
                                  cursor: 'pointer',
                                  padding: '0 2px',
                                  fontSize: '1rem',
                                  lineHeight: 1,
                                  flexShrink: 0,
                                }}
                                aria-label="Remove field"
                                title="Remove field"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>

                    {/* Add new field row */}
                    <div className="d-flex align-items-center gap-2 mt-2" style={{ flexWrap: 'wrap' }}>
                      {sections.length > 1 && (
                        <select
                          className="form-select form-select-sm"
                          style={{ ...inputStyle, flex: '0 0 auto', minWidth: 120, maxWidth: 160 }}
                          value={newSectionTarget}
                          onChange={(e) => setNewSectionTarget(e.target.value)}
                        >
                          {sections.map((s) => (
                            <option key={s.id} value={s.id}>{formatSectionLabel(s.id)}</option>
                          ))}
                        </select>
                      )}
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{ ...inputStyle, flex: '0 0 30%', minWidth: 120 }}
                        placeholder="Field name"
                        value={newKey}
                        onChange={(e) => setNewKey(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addItem()}
                      />
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{ ...inputStyle, flex: 1, minWidth: 100 }}
                        placeholder="Value"
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addItem()}
                      />
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={addItem}
                        disabled={!newKey.trim()}
                        style={{
                          backgroundColor: dark.card,
                          border: `1px solid ${dark.border}`,
                          color: dark.gold,
                          fontSize: '0.75rem',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                      >
                        <i className="bi bi-plus me-1" />
                        Add Field
                      </button>
                    </div>
                  </div>

                  {/* Right: documents analyzed */}
                  <div className="col-md-5">
                    <div style={labelStyle}>Documents Analyzed</div>
                    <div
                      style={{
                        border: `1px solid ${dark.border}`,
                        borderRadius: 8,
                        overflow: 'hidden',
                      }}
                    >
                      {/* Tab headers */}
                      <div
                        className="d-flex"
                        style={{ borderBottom: `1px solid ${dark.border}`, overflowX: 'auto' }}
                      >
                        {files.map((f, idx) => (
                          <button
                            key={f.name}
                            type="button"
                            onClick={() => setActiveDocTab(idx)}
                            style={{
                              background: 'none',
                              border: 'none',
                              borderBottom: activeDocTab === idx ? `2px solid ${dark.gold}` : '2px solid transparent',
                              color: activeDocTab === idx ? dark.goldBright : dark.muted,
                              fontSize: '0.7rem',
                              letterSpacing: '0.04em',
                              padding: '6px 12px',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                            }}
                          >
                            {f.name.length > 20 ? `${f.name.slice(0, 18)}…` : f.name}
                          </button>
                        ))}
                      </div>

                      {/* Tab body */}
                      {files[activeDocTab] && (
                        <div style={{ padding: 14, backgroundColor: dark.card }}>
                          <div className="d-flex align-items-center gap-2 mb-3">
                            <i
                              className="bi bi-file-earmark-text"
                              style={{ color: dark.gold, fontSize: '1.4rem' }}
                            />
                            <div>
                              <div style={{ fontSize: '0.8rem', fontWeight: 600, wordBreak: 'break-all' }}>
                                {files[activeDocTab].name}
                              </div>
                              <div style={{ fontSize: '0.7rem', color: dark.muted }}>
                                {fmtBytes(files[activeDocTab].size)}
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: '0.72rem',
                              color: dark.muted,
                              borderTop: `1px solid ${dark.border}`,
                              paddingTop: 10,
                            }}
                          >
                            <div style={{ marginBottom: 4 }}>
                              <span style={{ color: dark.mutedLight }}>Type: </span>
                              {files[activeDocTab].type || 'Unknown'}
                            </div>
                            <div>
                              <span style={{ color: dark.mutedLight }}>Last modified: </span>
                              {new Date(files[activeDocTab].lastModified).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div
                      style={{
                        marginTop: 10,
                        padding: '8px 12px',
                        backgroundColor: dark.card,
                        border: `1px solid ${dark.border}`,
                        borderRadius: 6,
                        fontSize: '0.72rem',
                        color: dark.muted,
                      }}
                    >
                      <i className="bi bi-info-circle me-1" style={{ color: dark.gold }} />
                      Review the extracted values on the left. Edit any fields that look incorrect, remove irrelevant ones, or add missing data before saving.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ================================================================
                STEP 4 — Verify & Save
            ================================================================ */}
            {step === 'compare' && (
              <div className="modal-body" style={{ padding: '16px 24px' }}>
                {/* Loading */}
                {compareLoading && (
                  <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 200 }}>
                    <div className="spinner-border mb-3" style={{ color: dark.gold, width: '2.5rem', height: '2.5rem' }} role="status" />
                    <div style={{ fontSize: '0.9rem', color: dark.goldBright }}>Checking past records…</div>
                  </div>
                )}

                {/* Error */}
                {!compareLoading && compareError && (
                  <div
                    className="alert py-2 mb-3"
                    style={{ backgroundColor: '#2a0800', border: '1px solid #8b3300', color: '#ff9966', fontSize: '0.83rem' }}
                  >
                    <i className="bi bi-exclamation-triangle me-2" />
                    {compareError}
                    <div style={{ marginTop: 6, fontSize: '0.78rem', color: '#cc7755' }}>
                      You can go back and re-try, or confirm to save anyway.
                    </div>
                  </div>
                )}

                {/* Results */}
                {!compareLoading && compareResults && (() => {
                  const newCount = compareResults.quarters.filter((q) => q.is_new).length;
                  const existingCount = compareResults.quarters.filter((q) => q.in_ddb).length;
                  return (
                    <>
                      {/* Summary banner */}
                      <div
                        style={{
                          padding: '8px 14px',
                          backgroundColor: dark.card,
                          border: `1px solid ${dark.border}`,
                          borderRadius: 7,
                          marginBottom: 14,
                          fontSize: '0.8rem',
                          color: dark.mutedLight,
                        }}
                      >
                        <i className="bi bi-bar-chart-steps me-2" style={{ color: dark.gold }} />
                        <strong style={{ color: dark.goldBright }}>{compareResults.quarters.length}</strong>
                        {' '}quarter{compareResults.quarters.length !== 1 ? 's' : ''} found in this document
                        {existingCount > 0 && (
                          <span> &mdash; <strong style={{ color: '#e8c060' }}>{existingCount}</strong> compared against stored records</span>
                        )}
                        {newCount > 0 && (
                          <span> &mdash; <strong style={{ color: '#6dbb88' }}>{newCount}</strong> new (will be saved)</span>
                        )}
                      </div>

                      {/* Per-quarter blocks */}
                      {compareResults.quarters.map((q) => {
                        const matchCount = q.comparison.filter((f) => f.match && f.extracted !== null && f.stored !== null).length;
                        const mismatchCount = q.comparison.filter((f) => !f.match).length;
                        const onlyA = q.comparison.filter((f) => f.extracted !== null && f.stored === null).length;
                        const onlyB = q.comparison.filter((f) => f.extracted === null && f.stored !== null).length;

                        return (
                          <div
                            key={q.period_end_date}
                            style={{
                              border: `1px solid ${q.is_new ? '#2a5a3a' : dark.border}`,
                              borderRadius: 8,
                              marginBottom: 14,
                              overflow: 'hidden',
                            }}
                          >
                            {/* Quarter header */}
                            <div
                              className="d-flex align-items-center justify-content-between"
                              style={{
                                padding: '7px 14px',
                                backgroundColor: q.is_new ? '#0a1f14' : '#0f0c02',
                                borderBottom: `1px solid ${q.is_new ? '#2a5a3a' : dark.border}`,
                              }}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <span style={{ fontWeight: 700, fontSize: '0.83rem', color: q.is_new ? '#6dbb88' : dark.goldBright }}>
                                  {q.period_end_date}
                                </span>
                                {q.section_key && (
                                  <span style={{ fontSize: '0.68rem', color: dark.muted }}>
                                    [{q.section_key}]
                                  </span>
                                )}
                              </div>
                              <span
                                style={{
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  letterSpacing: '0.08em',
                                  textTransform: 'uppercase',
                                  padding: '2px 8px',
                                  borderRadius: 4,
                                  backgroundColor: q.is_new ? '#1a4a2a' : '#2a2000',
                                  color: q.is_new ? '#6dbb88' : dark.gold,
                                  border: `1px solid ${q.is_new ? '#2a5a3a' : dark.border}`,
                                }}
                              >
                                {q.is_new ? '\u25CF New — Will be saved' : 'Existing — Verified'}
                              </span>
                            </div>

                            {/* New quarter: brief note */}
                            {q.is_new && (
                              <div style={{ padding: '10px 14px', fontSize: '0.78rem', color: '#5aaa78', backgroundColor: '#070f0a' }}>
                                <i className="bi bi-cloud-arrow-up me-2" />
                                No prior record found in the database. This quarter will be saved on confirm.
                              </div>
                            )}

                            {/* Existing quarter: diff table */}
                            {q.in_ddb && q.comparison.length > 0 && (
                              <>
                                {/* Stats row */}
                                <div
                                  style={{
                                    padding: '5px 14px',
                                    display: 'flex',
                                    gap: 16,
                                    fontSize: '0.7rem',
                                    backgroundColor: '#0b0900',
                                    borderBottom: `1px solid ${dark.border}`,
                                  }}
                                >
                                  <span style={{ color: '#6dbb88' }}><i className="bi bi-check2 me-1" />{matchCount} match</span>
                                  <span style={{ color: mismatchCount > 0 ? '#e06060' : dark.muted }}>
                                    <i className="bi bi-x-lg me-1" />{mismatchCount} mismatch
                                  </span>
                                  {onlyA > 0 && <span style={{ color: '#d4a820' }}><i className="bi bi-arrow-right me-1" />{onlyA} only in doc</span>}
                                  {onlyB > 0 && <span style={{ color: '#8888cc' }}><i className="bi bi-arrow-left me-1" />{onlyB} only in stored</span>}
                                </div>

                                {/* Comparison table */}
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.73rem' }}>
                                    <thead>
                                      <tr style={{ backgroundColor: '#0d0b02', borderBottom: `1px solid ${dark.border}` }}>
                                        <th style={{ padding: '5px 12px', color: dark.mutedLight, fontWeight: 600, textAlign: 'left', width: '36%' }}>Field</th>
                                        <th style={{ padding: '5px 12px', color: '#d4a820', fontWeight: 600, textAlign: 'left', width: '28%' }}>Report A (Extracted)</th>
                                        <th style={{ padding: '5px 12px', color: '#8888cc', fontWeight: 600, textAlign: 'left', width: '28%' }}>Report B (Stored)</th>
                                        <th style={{ padding: '5px 8px', textAlign: 'center', width: '8%' }}></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {q.comparison.map((f, fi) => {
                                        const bothNull = f.extracted === null && f.stored === null;
                                        const onlyInA = f.extracted !== null && f.stored === null;
                                        const onlyInB = f.extracted === null && f.stored !== null;
                                        const mismatch = !f.match && !bothNull;
                                        const rowBg = mismatch
                                          ? (fi % 2 === 0 ? '#1a0a0a' : '#160808')
                                          : (fi % 2 === 0 ? dark.card : '#0c0b02');
                                        return (
                                          <tr key={fi} style={{ backgroundColor: rowBg, borderBottom: `1px solid ${dark.border}` }}>
                                            <td style={{ padding: '5px 12px', color: dark.mutedLight, fontFamily: 'monospace', fontSize: '0.68rem' }}>
                                              {f.key}
                                            </td>
                                            <td style={{ padding: '5px 12px', color: onlyInA ? '#d4a820' : (mismatch ? '#e07070' : dark.text) }}>
                                              {f.extracted !== null ? f.extracted : <span style={{ color: dark.muted, fontStyle: 'italic' }}>null</span>}
                                            </td>
                                            <td style={{ padding: '5px 12px', color: onlyInB ? '#8888cc' : (mismatch ? '#e07070' : dark.text) }}>
                                              {f.stored !== null ? f.stored : <span style={{ color: dark.muted, fontStyle: 'italic' }}>null</span>}
                                            </td>
                                            <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                                              {bothNull ? (
                                                <span style={{ color: dark.muted, fontSize: '0.65rem' }}>—</span>
                                              ) : onlyInA ? (
                                                <span title="Only in extracted" style={{ color: '#d4a820' }}><i className="bi bi-arrow-right" /></span>
                                              ) : onlyInB ? (
                                                <span title="Only in stored" style={{ color: '#8888cc' }}><i className="bi bi-arrow-left" /></span>
                                              ) : f.match ? (
                                                <span title="Match" style={{ color: '#6dbb88' }}><i className="bi bi-check2" /></span>
                                              ) : (
                                                <span title="Mismatch" style={{ color: '#e06060', fontWeight: 700 }}>≠</span>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </>
                            )}

                            {/* Existing but no comparison data */}
                            {q.in_ddb && q.comparison.length === 0 && (
                              <div style={{ padding: '10px 14px', fontSize: '0.78rem', color: dark.muted }}>
                                <i className="bi bi-database-check me-2" style={{ color: '#8888cc' }} />
                                Already in database — this document did not provide extractable comparison data for this period. Will not be re-saved.
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {compareResults.quarters.length === 0 && (
                        <div style={{ padding: '20px', textAlign: 'center', color: dark.muted, fontSize: '0.8rem' }}>
                          No quarter data identified. The full extraction will be saved.
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* Footer */}
            <div
              className="modal-footer"
              style={{ borderTop: `1px solid ${dark.border}`, gap: 8 }}
            >
              {step === 'upload' && (
                <>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={onClose}
                    style={{
                      backgroundColor: 'transparent',
                      border: `1px solid ${dark.border}`,
                      color: dark.muted,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={handleAnalyze}
                    disabled={files.length === 0 || !selectedPromptId}
                    style={{
                      backgroundColor: files.length > 0 && selectedPromptId ? dark.gold : '#2a2200',
                      border: `1px solid ${files.length > 0 && selectedPromptId ? dark.gold : dark.border}`,
                      color: files.length > 0 && selectedPromptId ? '#0a0900' : dark.muted,
                      fontWeight: 700,
                      fontSize: '0.82rem',
                    }}
                  >
                    <i className="bi bi-cpu me-2" />
                    Analyze
                  </button>
                </>
              )}

              {step === 'analyzing' && (
                <span style={{ fontSize: '0.8rem', color: dark.muted }}>
                  Analysis in progress — please wait…
                </span>
              )}

              {step === 'review' && (
                <>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => setStep('upload')}
                    style={{
                      backgroundColor: 'transparent',
                      border: `1px solid ${dark.border}`,
                      color: dark.muted,
                    }}
                  >
                    <i className="bi bi-arrow-left me-1" />
                    Back
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={handleVerify}
                    disabled={submitting || totalItems === 0}
                    style={{
                      backgroundColor: totalItems > 0 ? dark.gold : '#2a2200',
                      border: `1px solid ${totalItems > 0 ? dark.gold : dark.border}`,
                      color: totalItems > 0 ? '#0a0900' : dark.muted,
                      fontWeight: 700,
                      fontSize: '0.82rem',
                    }}
                  >
                    <i className="bi bi-arrow-right-circle me-2" />
                    Next: Verify
                  </button>
                </>
              )}
              {step === 'compare' && (
                <>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => setStep('review')}
                    disabled={compareLoading}
                    style={{
                      backgroundColor: 'transparent',
                      border: `1px solid ${dark.border}`,
                      color: dark.muted,
                    }}
                  >
                    <i className="bi bi-arrow-left me-1" />
                    Back
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={handleConfirmSave}
                    disabled={compareLoading}
                    style={{
                      backgroundColor: compareLoading ? '#2a2200' : dark.gold,
                      border: `1px solid ${compareLoading ? dark.border : dark.gold}`,
                      color: compareLoading ? dark.muted : '#0a0900',
                      fontWeight: 700,
                      fontSize: '0.82rem',
                    }}
                  >
                    <i className="bi bi-check2-circle me-2" />
                    Confirm &amp; Save
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
